/**
 * This component is used to render a Rangle slider across application
 * @param { number } count - The number of lines of skeletons to render. If count is a decimal number like 3.5, three full skeletons and one half-width skeleton will be rendered.
 * @param { boolean } circle - Makes the skeleton circular by setting border-radius to 50%.
 * @param { string } className - A custom class name for the individual skeleton elements which is used alongside the default class, react-loading-skeleton.
 * @param { string } containerClassName - A custom class name for the <span> that wraps the individual skeleton elements.
 * @param { React.CSSProperties } style - This is an escape hatch for advanced use cases and is not the preferred way to style the skeleton. Props (e.g. width, borderRadius) take priority over this style object.
 * @param { string | number } width - The width of the skeleton.
 * @param { string | number } height - The height of each skeleton line.
 * @param { string | number } borderRadius - The border radius of the skeleton.
 * @param { boolean } inline - By default, a <br /> is inserted after each skeleton so that each skeleton gets its own line. When inline is true, no line breaks are inserted.
 * @param { number } duration - The length of the animation in seconds.
 * @param { string | number } direction - The direction of the animation, either left-to-right or right-to-left.
 * @param { boolean } enableAnimation - Whether the animation should play. The skeleton will be a solid color when this is false. You could use this prop to stop the animation if an error occurs.
 */

import React, { FunctionComponent } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export interface CustomProps {
    count?: number;
    circle?: boolean;
    className?: string;
    containerClassName?: string;
    style?: React.CSSProperties;
    width?: string | number;
    height?: string | number;
    borderRadius?: string | number;
    inline?: boolean;
    duration?: number;
    direction?: "ltr" | "rtl";
    enableAnimation?: boolean;
}

export const SkeletonLoader: FunctionComponent<CustomProps> = ({
    count,
    circle,
    className,
    containerClassName,
    style,
    width,
    height,
    borderRadius,
    inline,
    duration,
    direction,
    enableAnimation
}) => (
    <div className="h-100">
        <Skeleton
            borderRadius={borderRadius}
            circle={circle}
            className={className}
            containerClassName={containerClassName}
            count={count}
            direction={direction}
            duration={duration}
            enableAnimation={enableAnimation}
            height={height}
            inline={inline}
            style={style}
            width={width}
        />
    </div>
);

SkeletonLoader.defaultProps = {
    borderRadius: 8,
    count: 8,
    direction: "ltr",
    enableAnimation: true,
    height: 60,
    style: {
        marginBottom: 8
    }
};

export default SkeletonLoader;
